import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';
import { classLabel } from '../../config/dofusClasses';
import Icon from '../ui/Icon';
import ServerIcon from '../ui/ServerIcon';
import './Personnages.css';

const normalize = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const avatarUrl = (id, avatar) => (avatar ? `https://cdn.discordapp.com/avatars/${id}/${avatar}.png?size=64` : null);

/**
 * Bouton Admin du site : recensement de tous les serveurs, un compte Discord → ses personnages, serveur par serveur.
 * Lecture seule : les demandes se valident sur la page Personnages de chaque serveur.
 */
const SiteCensusSection = () => {
    const { t } = useLanguage();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [serverFilter, setServerFilter] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        api.get('/admin/personnages')
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const serverName = useCallback((id) => data?.servers.find(s => s.id === id)?.name || id, [data]);

    // Un compte → ses personnages, regroupés par serveur
    const accounts = useMemo(() => {
        if (!data) return [];
        const byOwner = new Map();
        data.personnages.forEach(p => p.owners.forEach(ownerId => {
            if (!byOwner.has(ownerId)) byOwner.set(ownerId, []);
            byOwner.get(ownerId).push(p);
        }));
        return [...byOwner.entries()]
            .map(([discordId, personnages]) => ({
                discordId,
                name: data.players[discordId]?.name || t('personnages.unknownPlayer', 'Joueur {id}', { id: discordId.slice(-4) }),
                avatar: data.players[discordId]?.avatar || null,
                personnages
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [data, t]);

    const visible = useMemo(() => {
        const q = normalize(search.trim());
        return accounts
            .map(account => {
                const accountMatch = !q || normalize(account.name).includes(q) || account.discordId.includes(q);
                const personnages = account.personnages.filter(p =>
                    (!serverFilter || p.serverId === serverFilter) && (accountMatch || normalize(p.name).includes(q)));
                return { ...account, personnages };
            })
            .filter(account => account.personnages.length > 0);
    }, [accounts, search, serverFilter]);

    const pendingServers = Object.entries(data?.pending || {});
    const serversWithCharacters = useMemo(
        () => [...new Set((data?.personnages || []).map(p => p.serverId))].sort((a, b) => serverName(a).localeCompare(serverName(b))),
        [data, serverName]
    );

    return (
        <section className="c2-card c2-section">
            <div className="c2-section__head">
                <h2 className="c2-section__title"><Icon name="users" size={20} />{t('navigation.personnages', 'Personnages')}</h2>
                <div className="perso-filters">
                    <select className="c2-select c2-input--sm" value={serverFilter} onChange={e => setServerFilter(e.target.value)} aria-label={t('personnages.serverFilter', 'Serveur')}>
                        <option value="">{t('personnages.allServers', 'Tous les serveurs')}</option>
                        {serversWithCharacters.map(id => <option key={id} value={id}>{serverName(id)}</option>)}
                    </select>
                    <input className="c2-input c2-input--sm" type="search" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder={t('personnages.siteSearchPlaceholder', 'Pseudo, ID Discord ou personnage...')} />
                    <button type="button" className="c2-icon-btn c2-icon-btn--sm" onClick={load} disabled={loading} aria-label={t('common.refresh', 'Actualiser')} title={t('common.refresh', 'Actualiser')}>
                        <Icon name="refresh" size={16} />
                    </button>
                </div>
            </div>

            {data && (
                <p className="c2-section__desc">
                    {t('personnages.siteSummary', '{players} comptes Discord · {personnages} personnages recensés sur {servers} serveur(s).', {
                        players: accounts.length, personnages: data.personnages.length, servers: serversWithCharacters.length
                    })}
                    {pendingServers.length > 0 && (
                        <span className="perso-site__pending">
                            {' '}{t('personnages.sitePending', 'Demandes en attente :')}{' '}
                            {pendingServers.map(([serverId, count], i) => (
                                <React.Fragment key={serverId}>
                                    {i > 0 && ', '}
                                    <Link to={`/server/${serverId}/personnages`}>{serverName(serverId)} ({count})</Link>
                                </React.Fragment>
                            ))}
                        </span>
                    )}
                </p>
            )}

            {loading && !data ? (
                <div className="c2-loading"><div className="c2-spinner" /><p>{t('common.loading', 'Chargement...')}</p></div>
            ) : !data ? (
                <p className="c2-empty">{t('personnages.loadError', 'Impossible de charger les personnages.')}</p>
            ) : visible.length === 0 ? (
                <p className="c2-muted">{accounts.length === 0 ? t('personnages.noPlayers', 'Aucun personnage recensé pour l\'instant.') : t('personnages.noMatch', 'Aucun résultat.')}</p>
            ) : (
                <ul className="perso-site">
                    {visible.map(account => {
                        const byServer = new Map();
                        account.personnages.forEach(p => {
                            if (!byServer.has(p.serverId)) byServer.set(p.serverId, []);
                            byServer.get(p.serverId).push(p);
                        });
                        const avatar = avatarUrl(account.discordId, account.avatar);
                        return (
                            <li key={account.discordId} className="c2-tile perso-site__account">
                                <div className="perso-site__who">
                                    {avatar
                                        ? <img className="perso-site__avatar" src={avatar} alt="" />
                                        : <span className="perso-site__avatar perso-site__avatar--empty"><Icon name="user" size={16} /></span>}
                                    <div className="perso-site__text">
                                        <strong>{account.name}</strong>
                                        <span className="c2-muted">{account.discordId}</span>
                                    </div>
                                </div>
                                <div className="perso-site__servers">
                                    {[...byServer.entries()].map(([serverId, personnages]) => {
                                        const server = data.servers.find(s => s.id === serverId);
                                        return (
                                            <div key={serverId} className="perso-site__server">
                                                <Link to={`/server/${serverId}/personnages`} className="perso-site__server-name">
                                                    {server && <ServerIcon guild={server} size={20} />}
                                                    {serverName(serverId)}
                                                </Link>
                                                <ul className="perso-chips">
                                                    {personnages.map(p => {
                                                        const info = classLabel(t, p.classe);
                                                        const coOwners = p.owners.filter(id => id !== account.discordId);
                                                        return (
                                                            <li key={p._id} className={`perso-chip${coOwners.length ? ' perso-chip--shared' : ''}`}
                                                                title={coOwners.length ? t('personnages.sharedWith', 'Partagé avec {players}', { players: coOwners.map(id => data.players[id]?.name || id).join(', ') }) : undefined}>
                                                                <span className="perso-chip__name">{p.name}</span>
                                                                {info && <span className="c2-muted">{info}</span>}
                                                                {coOwners.length > 0 && <Icon name="users" size={13} />}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        );
                                    })}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
};

export default SiteCensusSection;
